// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext } from '../types';
import { partyStyle } from '../parties';
import { niceMax, axisTicks } from '../charts';
import { fmtDollars, fmtMoney, fmtQuarter, escapeHtml } from '../format';

export function renderTrends(root: HTMLElement, ctx: AppContext): void {
  const { data } = ctx;
  const q = data.summary.quarterly;

  root.innerHTML = `
    <div class="view-head">
      <h2>Spending over time</h2>
      <p>Total quarterly expenses across all parliamentarians, and how the major parties track quarter to quarter. The 2025 federal election (May 2025) is marked — spending shifts as new members arrive and others leave.</p>
    </div>
    <div class="chart-card" style="margin-bottom:var(--space-lg)">
      <h3>Total expenses per quarter</h3>
      <div class="subtitle">All parliamentarians combined, ${escapeHtml(q[0]?.label || '')} to ${escapeHtml(q[q.length - 1]?.label || '')}.</div>
      <div id="t-bars"></div>
    </div>
    <div class="chart-card">
      <h3>Major parties over time</h3>
      <div class="subtitle">Combined quarterly spend for the five largest parties. Hover a point for the exact figure.</div>
      <div id="t-lines"></div>
      <div class="legend" id="t-legend"></div>
    </div>`;

  drawBars(document.getElementById('t-bars')!, q);
  drawLines(document.getElementById('t-lines')!, document.getElementById('t-legend')!, data);
}

const ELECTION_Q = '2025Q02';

function drawBars(el: HTMLElement, q: AppContext['data']['summary']['quarterly']): void {
  const W = 900, H = 320, ml = 56, mr = 12, mt = 12, mb = 46;
  const iw = W - ml - mr, ih = H - mt - mb;
  const max = niceMax(Math.max(...q.map((x) => x.total), 1));
  const bw = (iw / q.length) * 0.68;
  const ticks = axisTicks(max, 4);
  const grid = ticks
    .map((t) => {
      const y = mt + ih - (t / max) * ih;
      return `<line class="grid-line" x1="${ml}" y1="${y}" x2="${W - mr}" y2="${y}"></line><text class="axis-label" x="${ml - 8}" y="${y + 3}" text-anchor="end">${fmtMoney(t)}</text>`;
    })
    .join('');
  const bars = q
    .map((d, i) => {
      const x = ml + (iw / q.length) * i + ((iw / q.length) - bw) / 2;
      const h = (d.total / max) * ih;
      const y = mt + ih - h;
      const isEl = d.q === ELECTION_Q;
      const tip = `${escapeHtml(fmtQuarter(d.q))}: ${fmtDollars(d.total)}`;
      return `<rect class="bar-el" x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(0, h).toFixed(1)}" rx="2" fill="${isEl ? '#a4243b' : '#1c3a5e'}" data-tip="${tip}" aria-label="${tip}"></rect>
        <text class="axis-label" x="${(x + bw / 2).toFixed(1)}" y="${H - mb + 16}" text-anchor="middle">${d.short}</text>`;
    })
    .join('');
  const elx = ml + (iw / q.length) * (q.findIndex((d) => d.q === ELECTION_Q) + 0.5);
  const elMark = q.some((d) => d.q === ELECTION_Q)
    ? `<line x1="${elx}" y1="${mt}" x2="${elx}" y2="${mt + ih}" stroke="#a4243b" stroke-width="1" stroke-dasharray="3 3"></line><text class="axis-label" x="${elx}" y="${mt + 10}" text-anchor="middle" fill="#a4243b">2025 election</text>`
    : '';
  el.innerHTML = `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Quarterly total expenses">${grid}${elMark}<line class="axis-line" x1="${ml}" y1="${mt + ih}" x2="${W - mr}" y2="${mt + ih}"></line>${bars}</svg>`;
}

function drawLines(el: HTMLElement, legendEl: HTMLElement, data: AppContext['data']): void {
  const q = data.summary.quarterly;
  const parties = data.summary.parties.filter((p) => p.code !== 'FMR').slice(0, 5);
  const W = 900, H = 340, ml = 56, mr = 14, mt = 14, mb = 46;
  const iw = W - ml - mr, ih = H - mt - mb;
  const max = niceMax(Math.max(...q.flatMap((d) => parties.map((p) => d.byParty[p.code] || 0)), 1));
  const xAt = (i: number) => ml + (q.length === 1 ? iw / 2 : (i / (q.length - 1)) * iw);
  const yAt = (v: number) => mt + ih - (v / max) * ih;
  const ticks = axisTicks(max, 4);
  const grid = ticks
    .map((t) => {
      const y = yAt(t);
      return `<line class="grid-line" x1="${ml}" y1="${y}" x2="${W - mr}" y2="${y}"></line><text class="axis-label" x="${ml - 8}" y="${y + 3}" text-anchor="end">${fmtMoney(t)}</text>`;
    })
    .join('');
  const xlabels = q
    .map((d, i) => `<text class="axis-label" x="${xAt(i).toFixed(1)}" y="${H - mb + 16}" text-anchor="middle">${d.short}</text>`)
    .join('');
  const series = parties
    .map((p) => {
      const col = partyStyle(p.code).colour;
      const pts = q.map((d, i) => [xAt(i), yAt(d.byParty[p.code] || 0)] as [number, number]);
      const path = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
      const dots = pts
        .map(([x, y], i) => {
          const v = q[i].byParty[p.code] || 0;
          const tip = `${escapeHtml(p.name)} — ${escapeHtml(fmtQuarter(q[i].q))}: ${fmtDollars(v)}`;
          return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3.5" fill="${col}" stroke="#fff" stroke-width="1" data-tip="${tip}" aria-label="${tip}"></circle>`;
        })
        .join('');
      return `<polyline points="${path}" fill="none" stroke="${col}" stroke-width="2" stroke-linejoin="round"></polyline>${dots}`;
    })
    .join('');
  el.innerHTML = `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Party spending over time">${grid}${xlabels}<line class="axis-line" x1="${ml}" y1="${mt + ih}" x2="${W - mr}" y2="${mt + ih}"></line>${series}</svg>`;
  legendEl.innerHTML = parties
    .map((p) => `<span class="item"><span class="swatch" style="background:${partyStyle(p.code).colour}"></span>${escapeHtml(p.name)}</span>`)
    .join('');
}
